import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LOGO_URL =
  '/api/static/brand/logo-h0.png';

const LANGS = [
  { code: 'ca', label: 'CA' },
  { code: 'es', label: 'ES' },
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
];

/** Get a text for the active language from a multilingual block {ca,es,en,fr}. */
const ml = (block, lang) => {
  if (!block) return '';
  return block[lang] || block.ca || block.es || block.en || block.fr || '';
};

const Section = ({ children, className = '', delay = 0, testid }) => (
  <motion.section
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
    data-testid={testid}
  >
    {children}
  </motion.section>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3 text-[#F2E8D5]/90 font-sans font-light leading-relaxed">
    <span className="mt-1 flex-shrink-0 w-5 h-5 rounded-full border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] text-[10px]">
      ✓
    </span>
    <span>{children}</span>
  </li>
);

export const SigFunnel = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'ca';
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/sig-content`);
        setContent(r.data);
      } catch (e) {
        console.error('Failed to load SIG content', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleWhatsApp = () => {
    if (!content) return;
    const num = content.whatsapp_number || '34664368737';
    const msg = encodeURIComponent(ml(content.whatsapp_message, lang));
    window.open(`https://wa.me/${num}?text=${msg}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#0E4D2A] flex items-center justify-center text-[#A8B4A6] font-sans text-sm tracking-widest">
        ...
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen w-full bg-[#0E4D2A] flex items-center justify-center text-[#F2E8D5] font-sans">
        Error loading content
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen w-full bg-[#0E4D2A] text-[#F2E8D5] overflow-x-hidden"
      data-testid="sig-funnel-page"
    >
      {/* Subtle watermark */}
      <div className="pointer-events-none fixed inset-0 z-0 flex items-center justify-center select-none">
        <span
          className="font-serif font-black text-[#083B1F]"
          style={{ fontSize: 'min(75vw, 75vh)', lineHeight: 1, opacity: 0.35 }}
          aria-hidden="true"
        >
          H0
        </span>
      </div>

      {/* Top nav: language only — this is a funnel, no escape but WhatsApp or closing the tab */}
      <div className="fixed top-0 left-0 right-0 z-30 flex items-center justify-end px-5 md:px-10 py-5">
        <div className="flex gap-3 md:gap-4 font-sans text-[11px] md:text-xs tracking-[0.25em]" data-testid="sig-lang-switcher">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => i18n.changeLanguage(l.code)}
              data-testid={`sig-lang-${l.code}`}
              className={`transition-colors duration-300 ${
                lang === l.code ? 'text-[#D4AF37]' : 'text-[#A8B4A6] hover:text-[#F2E8D5]'
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-10 pt-24 md:pt-32 pb-20">
        {/* HERO */}
        <Section testid="sig-hero">
          <div className="flex flex-col items-center text-center">
            <img src={LOGO_URL} alt="Hoyo Zero" className="w-20 sm:w-24 h-auto opacity-95" />

            <p className="mt-8 text-[#D4AF37] font-sans font-light tracking-[0.32em] uppercase text-[10px] sm:text-xs">
              {ml(content.eyebrow, lang)}
            </p>

            <h1 className="mt-6 font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
              {ml(content.hero_title, lang)}
            </h1>

            <p className="mt-6 font-serif italic text-[#A8B4A6] text-lg sm:text-xl md:text-2xl max-w-3xl">
              {ml(content.hero_subtitle, lang)}
            </p>

            {/* Partner logo (UGOLF Aravell) */}
            {content.partner_logo_url && (
              <img
                src={content.partner_logo_url}
                alt={content.partner_name}
                className="mt-8 h-16 sm:h-20 md:h-24 w-auto object-contain opacity-95"
                data-testid="sig-partner-logo"
              />
            )}

            {/* Price card */}
            <div className="mt-10 inline-flex items-center gap-5 border border-[#D4AF37]/40 rounded-full px-7 py-3 backdrop-blur-sm bg-[#083B1F]/40">
              <span className="font-serif text-3xl md:text-4xl text-[#D4AF37] tracking-tight">
                {content.price}
              </span>
              <span className="h-8 w-px bg-[#D4AF37]/40" />
              <span className="font-sans text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#F2E8D5]/90">
                {ml(content.price_label, lang)}
              </span>
            </div>

            <button
              type="button"
              onClick={handleWhatsApp}
              data-testid="sig-cta-hero"
              className="mt-8 group inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0E4D2A] font-sans font-medium tracking-[0.18em] uppercase text-xs md:text-sm px-8 py-4 rounded-full transition-all duration-500 shadow-[0_8px_30px_rgba(212,175,55,0.2)] hover:shadow-[0_12px_40px_rgba(212,175,55,0.35)]"
            >
              {ml(content.cta_text, lang)}
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </button>

            <p className="mt-12 max-w-2xl font-sans font-light text-[#F2E8D5]/80 leading-relaxed text-sm md:text-base">
              {ml(content.hero_intro, lang)}
            </p>
          </div>
        </Section>

        {/* SOCIAL PROOF */}
        <Section testid="sig-social-proof" className="mt-24 md:mt-32">
          <div className="flex flex-col items-center text-center">
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
            <div className="mt-8 flex flex-col items-center">
              <span className="font-serif text-7xl md:text-8xl text-[#D4AF37] leading-none tracking-tight">
                {content.social_proof_number}
              </span>
              <p className="mt-4 font-sans font-light text-[#F2E8D5]/90 text-base md:text-lg max-w-xl">
                {ml(content.social_proof_text, lang)}
              </p>
            </div>

            {/* Video / video placeholder */}
            {content.video_url ? (
              <div className="mt-10 w-full max-w-md mx-auto aspect-video rounded-2xl overflow-hidden border border-[#D4AF37]/20 bg-black shadow-[0_8px_40px_rgba(0,0,0,0.35)]">
                <video
                  key={`${content.video_url}#${content.video_start_seconds || 0}#${content.video_autoplay ? 'auto' : 'click'}`}
                  poster={content.video_poster_url || undefined}
                  controls
                  playsInline
                  autoPlay={!!content.video_autoplay}
                  muted={!!content.video_autoplay}
                  loop={!!content.video_autoplay}
                  preload={content.video_autoplay ? 'auto' : 'metadata'}
                  className="w-full h-full object-cover block"
                  data-testid="sig-video"
                >
                  {/* Explicit MIME type as a hint when the server returns a malformed Content-Type
                      (e.g. Cloudinary: `video/mp4;codecs=avc1` without profile). */}
                  <source src={`${content.video_url}#t=${content.video_start_seconds || 0}`} type="video/mp4" />
                  El teu navegador no pot reproduir aquest vídeo.
                </video>
              </div>
            ) : (
              <div
                className="mt-10 w-full max-w-md mx-auto aspect-video rounded-2xl border border-dashed border-[#D4AF37]/30 bg-[#083B1F]/40 flex items-center justify-center text-[#A8B4A6] font-serif italic text-sm md:text-base"
                data-testid="sig-video-placeholder"
              >
                · vídeo amb tots els alumnes · (pendent de pujar)
              </div>
            )}
          </div>
        </Section>

        {/* WHAT INCLUDED */}
        <Section testid="sig-included" className="mt-24 md:mt-32">
          <div className="border-l-2 border-[#D4AF37]/40 pl-6 md:pl-10">
            <p className="text-[#D4AF37] font-sans font-light tracking-[0.32em] uppercase text-[10px] sm:text-xs">
              ✦ {ml(content.eyebrow, lang).split('·')[0]?.trim()}
            </p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight">
              {ml(content.included_title, lang)}
            </h2>
          </div>
          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5 max-w-3xl mx-auto">
            {content.included_items.map((it, i) => (
              <Bullet key={i}>{ml(it, lang)}</Bullet>
            ))}
          </ul>
        </Section>

        {/* COURSE INFO */}
        <Section testid="sig-course" className="mt-24 md:mt-32">
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <p className="text-[#D4AF37] font-sans font-light tracking-[0.32em] uppercase text-[10px] sm:text-xs">
                {content.partner_name}
              </p>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight">
                {ml(content.course_title, lang)}
              </h2>
              <p className="mt-6 font-sans font-light text-[#F2E8D5]/85 leading-relaxed text-sm md:text-base">
                {ml(content.course_text, lang)}
              </p>
            </div>
            <ul className="space-y-4 md:pt-12">
              {content.course_bullets.map((b, i) => (
                <Bullet key={i}>{ml(b, lang)}</Bullet>
              ))}
            </ul>
          </div>
        </Section>

        {/* AFTER */}
        <Section testid="sig-after" className="mt-24 md:mt-32">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-[#D4AF37] font-sans font-light tracking-[0.32em] uppercase text-[10px] sm:text-xs">
              + Next
            </p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight italic">
              {ml(content.after_title, lang)}
            </h2>
            <p className="mt-6 font-sans font-light text-[#F2E8D5]/85 leading-relaxed text-sm md:text-base">
              {ml(content.after_text, lang)}
            </p>
          </div>
        </Section>

        {/* GALLERY (optional) */}
        {content.gallery && content.gallery.length > 0 && (
          <Section testid="sig-gallery" className="mt-24 md:mt-32">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {content.gallery.map((g, i) => (
                <div
                  key={i}
                  className="aspect-square overflow-hidden rounded-xl border border-[#D4AF37]/15 bg-[#083B1F]"
                >
                  <img
                    src={g.url}
                    alt={g.caption || `gallery-${i}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* FINAL CTA */}
        <Section testid="sig-final-cta" className="mt-28 md:mt-36">
          <div className="relative flex flex-col items-center text-center border border-[#D4AF37]/30 rounded-3xl p-10 md:p-16 bg-gradient-to-b from-[#083B1F]/60 to-[#0E4D2A]/30 backdrop-blur-sm overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />
            <p className="text-[#D4AF37] font-sans font-light tracking-[0.32em] uppercase text-[10px] sm:text-xs">
              ✦ Reserva
            </p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight max-w-3xl">
              {ml(content.hero_title, lang)}
            </h2>
            <div className="mt-6 inline-flex items-center gap-4">
              <span className="font-serif text-4xl md:text-5xl text-[#D4AF37]">{content.price}</span>
              <span className="h-8 w-px bg-[#D4AF37]/40" />
              <span className="font-sans text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#F2E8D5]/85">
                {ml(content.price_label, lang)}
              </span>
            </div>
            <button
              type="button"
              onClick={handleWhatsApp}
              data-testid="sig-cta-final"
              className="mt-8 group inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0E4D2A] font-sans font-medium tracking-[0.18em] uppercase text-xs md:text-sm px-9 py-4 rounded-full transition-all duration-500 shadow-[0_8px_30px_rgba(212,175,55,0.25)] hover:shadow-[0_12px_40px_rgba(212,175,55,0.4)]"
            >
              {ml(content.cta_text, lang)}
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </button>
            <p className="mt-5 text-[10px] md:text-xs tracking-widest uppercase text-[#A8B4A6]">
              WhatsApp · +34 664 368 737
            </p>
          </div>
        </Section>

        {/* Footer */}
        <p className="mt-20 text-center text-[10px] tracking-[0.3em] uppercase text-[#A8B4A6]/60 font-sans">
          © {new Date().getFullYear()} · Hoyo Zero · @joan_monzo · {content.partner_name}
        </p>
      </div>
    </div>
  );
};

export default SigFunnel;
